import { isNullOrUndefined } from './object-utils';
import { NO_CLOSEST_BOOKMARKS_FOUND } from 'constants/feature/section-details-constants';
import { checkIsArrayOfArrays } from 'utils/ocr-text-selection-utils';

export const getArrayWithNewSection = (
  tree,
  section,
  parentId,
  inline,
  isSectionAlreadyInTree,
) => {
  let isSectionAdded = false;
  if (tree.children) {
    tree.children.forEach((children, index) => {
      //case one: section is already in tree, wee need to add a child to the existing node with the same section id.
      if (isSectionAlreadyInTree) {
        // if current children has the same section id
        if (children.id === section.id && !children.isNew) {
          if (children.children) {
            if (inline) {
              tree.children.splice(index + 1, 0, section);
            } else {
              children.children.unshift(section);
            }
          } else {
            if (inline) {
              tree.children.splice(index + 1, 0, section);
            } else {
              children.children = [section];
            }
          }
          isSectionAdded = true;
          return isSectionAdded;
        } else {
          //if not, it calls himself with the new tree data
          if (!isSectionAdded) {
            isSectionAdded = getArrayWithNewSection(
              children,
              section,
              parentId,
              inline,
              isSectionAlreadyInTree,
            );
          }
        }
      } else {
        //case 2 the section id does not exist in the tree so we need to append the new section to a node
        if (children.id === parentId) {
          if (inline) {
            tree.children.splice(index + 1, 0, section);
          } else {
            const indentParent = tree.children[index];
            if (indentParent.children) {
              indentParent.children.push(section);
            } else {
              indentParent.children = [section];
            }
          }
          isSectionAdded = true;
          return isSectionAdded;
        } else {
          if (!isSectionAdded) {
            isSectionAdded = getArrayWithNewSection(
              children,
              section,
              parentId,
              inline,
              isSectionAlreadyInTree,
            );
          }
        }
      }
    });
  }
  return isSectionAdded;
};

export const getArrayWithNewSectionOCR = (
  tree,
  newBookmark,
  parentId,
  inline,
) => {
  // // Helper: checks if a bookmark with the same id already exists at this level
  function existsAtLevel(nodes, bookmark) {
    return nodes.some((node) => node.isNew);
  }

  if (parentId === NO_CLOSEST_BOOKMARKS_FOUND) {
    tree.unshift(newBookmark);
    return tree;
  }
  // If parentId is null or undefined, handle root-level logic using parentOrderNum
  if (parentId === null || parentId === 0) {
    const { parentOrderNum } = newBookmark;

    if (inline) {
      // Inline: add as sibling at root level, positioned after parentOrderNum
      if (!existsAtLevel(tree, newBookmark)) {
        newBookmark.parentId = null;

        if (parentOrderNum !== undefined && parentOrderNum !== null) {
          // Find the node with matching parentOrderNum
          const parentIndex = tree.findIndex(
            (node) => node.orderNum === parentOrderNum,
          );

          if (parentIndex !== -1) {
            // Insert directly after the parent node
            tree.splice(parentIndex + 1, 0, newBookmark);
          } else {
            // Parent node not found, add at end as fallback
            tree.push(newBookmark);
          }
        } else {
          // No parentOrderNum provided, add at end
          tree.push(newBookmark);
        }
      }
      return tree;
    } else {
      // Not inline: add as child of the node with matching orderNum
      if (parentOrderNum !== undefined && parentOrderNum !== null) {
        // Find the parent node with matching orderNum
        const parentNode = tree.find(
          (node) => node.orderNum === parentOrderNum,
        );

        if (parentNode) {
          // Add as child of the found parent
          if (!parentNode.children) parentNode.children = [];
          if (!existsAtLevel(parentNode.children, newBookmark)) {
            newBookmark.parentId = parentNode.id;
            parentNode.children.push(newBookmark);
          }
        } else {
          // Parent with parentOrderNum not found, fallback to original logic
          if (tree.length > 0) {
            let root = tree[0];
            if (!root.children) root.children = [];
            if (!existsAtLevel(root.children, newBookmark)) {
              newBookmark.parentId = root.id;
              root.children.push(newBookmark);
            }
          } else {
            // If tree is empty, add as root
            newBookmark.parentId = null;
            tree.push(newBookmark);
          }
        }
      } else {
        // No parentOrderNum provided, use original logic
        if (tree.length > 0) {
          let root = tree[0];
          if (!root.children) root.children = [];
          if (!existsAtLevel(root.children, newBookmark)) {
            newBookmark.parentId = root.id;
            root.children.push(newBookmark);
          }
        } else {
          // If tree is empty, add as root
          newBookmark.parentId = null;
          tree.push(newBookmark);
        }
      }
      return tree;
    }
  }

  for (let i = 0; i < tree.length; i++) {
    const node = tree[i];

    if (node.id === parentId) {
      if (node.children && Array.isArray(node.children)) {
        // If inline, add as sibling
        if (inline) {
          // Insert after closestBookmarkId among children
          const idx = node.children.findIndex(
            (child) => child.id === newBookmark.closestBookmarkId,
          );
          if (idx !== -1) {
            node.children.splice(idx + 1, 0, newBookmark);
          } else if (!existsAtLevel(node.children, newBookmark)) {
            node.children.push(newBookmark);
          }
        } else {
          // Not inline, add as child
          if (!node.children) node.children = [];
          node.children.unshift(newBookmark);
        }
        return tree;
      } else {
        if (inline) {
          tree.splice(i + 1, 0, newBookmark);
        } else {
          // Not inline, add as child
          if (!node.children) node.children = [];
          node.children.push(newBookmark);
        }
        return tree;
      }
    }
    // If not found, continue searching in children
    if (node.children && Array.isArray(node.children)) {
      const result = getArrayWithNewSectionOCR(
        node.children,
        newBookmark,
        parentId,
        inline,
      );
      if (result !== node.children || result.some((child) => child.isNew)) {
        return tree;
      }
    }
  }
  return tree;
};

export const deleteNewSectionOCR = (tree, bookmarkId) => {
  function removeRecursive(nodes, bookmarkId) {
    if (!Array.isArray(nodes)) return false;
    for (let i = nodes.length - 1; i >= 0; i--) {
      const node = nodes[i];
      // Only remove if id matches AND isNew is true
      if (node.id === bookmarkId && node.isNew) {
        nodes.splice(i, 1);
        // Don't return here, keep going in case there are multiple
      } else if (node.children && Array.isArray(node.children)) {
        removeRecursive(node.children, bookmarkId);
      }
    }
  }
  removeRecursive(tree, bookmarkId);
};
//function to delete section added when the modal opened and when the modal closes
export const deleteNewSection = (tree, sectionId) => {
  let isSectionDeleted = false;
  if (tree.children) {
    tree.children.forEach((children, index) => {
      if (children.id === sectionId && children.isNew) {
        if (tree.children.length === 1) {
          tree.children = null;
        } else {
          tree.children.splice(index, 1);
        }
        isSectionDeleted = true;
        return isSectionDeleted;
      } else {
        if (!isSectionDeleted) {
          isSectionDeleted = deleteNewSection(children, sectionId);
        }
      }
    });
  }
  return isSectionDeleted;
};
//function to verify if a section is already in the tree
export const isSectionInTree = (tree, parentId) => {
  let isSection = false;
  if (tree.id === parentId) {
    isSection = true;
    return isSection;
  } else {
    if (tree.children) {
      tree.children.forEach((children) => {
        if (!isSection) {
          isSection = isSectionInTree(children, parentId);
        }
      });
    }
  }
  return isSection;
};

//function to get tree in section
export const getTreeBySectionId = (tree, sectionId) => {
  let selectedTree = null;
  if (tree.id === sectionId) {
    selectedTree = tree;
    return selectedTree;
  } else {
    if (tree.children) {
      tree.children.forEach((children) => {
        if (!selectedTree) {
          selectedTree = getTreeBySectionId(children, sectionId);
        }
      });
    }
  }
  return selectedTree;
};

export const isRootLevelSection = (tree, parentId) => {
  let parentSection = getTreeBySectionId({ children: tree }, parentId);

  return (
    isNullOrUndefined(parentSection) ||
    isNullOrUndefined(parentSection.parentId)
  );
};

export const getBookmarkOrderNumber = ({
  sectionsHierarchy,
  contentSectionMap,
}) => {
  const contentSectionMapFlattenArray = Object.values(contentSectionMap.map)
    ?.flat()
    ?.filter((section) => !section?.deleted)
    ?.map((section) => section?.id);

  let orderNum = 1;

  if (!sectionsHierarchy || !Array.isArray(sectionsHierarchy)) {
    return;
  }

  for (let i = 0; i < sectionsHierarchy.length; i++) {
    // Skip deleted sections
    if (sectionsHierarchy[i]?.deleted === 1) {
      continue;
    }
    if (sectionsHierarchy[i].isNew) {
      const previousBookmarkOrderNum = sectionsHierarchy?.[i - 1]?.orderNum;
      const nextBookmarkOrderNum = sectionsHierarchy?.[i + 1]?.orderNum;

      if (sectionsHierarchy[i].id === 'NO_CLOSEST_BOOKMARKS_FOUND') {
        orderNum = Math.min(...contentSectionMapFlattenArray) - 0.5;
        return orderNum;
      } else if (previousBookmarkOrderNum && nextBookmarkOrderNum) {
        orderNum = (previousBookmarkOrderNum + nextBookmarkOrderNum) / 2;
        return orderNum;
      } else if (!previousBookmarkOrderNum && !nextBookmarkOrderNum) {
        orderNum = 1;
        return orderNum;
      } else if (previousBookmarkOrderNum && !nextBookmarkOrderNum) {
        orderNum = previousBookmarkOrderNum + 1;
        return orderNum;
      } else if (!previousBookmarkOrderNum && nextBookmarkOrderNum) {
        orderNum = nextBookmarkOrderNum - 0.1;
        return orderNum;
      }
    }

    if (
      sectionsHierarchy[i].children &&
      Array.isArray(sectionsHierarchy[i].children) &&
      sectionsHierarchy[i].children.length > 0
    ) {
      const childResult = getBookmarkOrderNumber({
        sectionsHierarchy: sectionsHierarchy[i].children,
        contentSectionMap: contentSectionMap,
      });
      // If we found a valid result in children, return it
      if (childResult !== undefined && childResult !== 1) {
        return childResult;
      }
    }
  }
  return orderNum;
};

export const checkIfExisitingBookmark = ({
  polygon,
  contentSectionMap,
  sectionId,
}) => {
  const selectedLines = polygon;
  const bookmarkList = contentSectionMap.get(sectionId);
  let containsBookmark = false;
  let bookmarkContained = {};

  // Round to 4 decimal places for better precision
  const roundToFourDecimals = (num) => Math.round(num * 10000) / 10000;

  // Helper function to get bounds from a single polygon
  const getPolygonBounds = (polygon) => {
    const [x1, y1, , , x3, y3] = polygon;
    return {
      minX: roundToFourDecimals(x1),
      maxX: roundToFourDecimals(x3),
      minY: roundToFourDecimals(y1),
      maxY: roundToFourDecimals(y3),
    };
  };

  // Helper function to check if two coordinates are approximately equal
  const areCoordinatesEqual = (coord1, coord2, threshold = 0.05) => {
    const rounded1 = roundToFourDecimals(coord1);
    const rounded2 = roundToFourDecimals(coord2);
    const diff = Math.abs(rounded1 - rounded2);
    return diff <= threshold;
  };

  // Helper function to check if two polygon bounds exactly match
  const doPolygonBoundsMatch = (bounds1, bounds2, threshold = 0.05) => {
    const minXMatch = areCoordinatesEqual(
      bounds1.minX,
      bounds2.minX,
      threshold,
    );
    const maxXMatch = areCoordinatesEqual(
      bounds1.maxX,
      bounds2.maxX,
      threshold,
    );
    const minYMatch = areCoordinatesEqual(
      bounds1.minY,
      bounds2.minY,
      threshold,
    );
    const maxYMatch = areCoordinatesEqual(
      bounds1.maxY,
      bounds2.maxY,
      threshold,
    );

    const isMatch = minXMatch && maxXMatch && minYMatch && maxYMatch;

    return isMatch;
  };

  // Helper function to check if a bookmark polygon matches any selected polygon
  const doesBookmarkPolygonMatchAnySelected = (
    bookmarkPolygon,
    selectedPolygons,
  ) => {
    const bookmarkBounds = getPolygonBounds(bookmarkPolygon);

    for (const selectedPolygon of selectedPolygons) {
      const selectedBounds = getPolygonBounds(selectedPolygon);
      if (doPolygonBoundsMatch(bookmarkBounds, selectedBounds)) {
        return true;
      }
    }
    return false;
  };

  if (bookmarkList && bookmarkList.length) {
    // Convert selectedLines to array of polygons
    let selectedPolygons = [];
    if (Array.isArray(selectedLines) && selectedLines.length === 8) {
      selectedPolygons = [selectedLines]; // Single polygon
    } else if (Array.isArray(selectedLines)) {
      selectedPolygons = selectedLines; // Multiple polygons
    }

    // Process bookmark objects
    for (const bookmark of bookmarkList) {
      if (bookmark.deleted) {
        const { polygon: bookmarkListPolygon } = bookmark;

        let parsedPolygon = bookmarkListPolygon;
        if (typeof bookmarkListPolygon === 'string') {
          try {
            parsedPolygon = JSON.parse(bookmarkListPolygon);
          } catch (e) {
            continue;
          }
        }

        let bookmarkPolygons = [];
        if (checkIsArrayOfArrays(parsedPolygon)) {
          bookmarkPolygons = parsedPolygon;
        } else if (Array.isArray(parsedPolygon) && parsedPolygon.length === 8) {
          bookmarkPolygons = [parsedPolygon];
        } else {
          continue;
        }

        // Check if ALL bookmark polygons match with selected polygons
        const allBookmarkPolygonsMatch = bookmarkPolygons.every(
          (bookmarkPolygon) => {
            return doesBookmarkPolygonMatchAnySelected(
              bookmarkPolygon,
              selectedPolygons,
            );
          },
        );

        // Also check if ALL selected polygons are matched by bookmark polygons
        const allSelectedPolygonsMatch = selectedPolygons.every(
          (selectedPolygon) => {
            const selectedBounds = getPolygonBounds(selectedPolygon);
            return bookmarkPolygons.some((bookmarkPolygon) => {
              const bookmarkBounds = getPolygonBounds(bookmarkPolygon);
              return doPolygonBoundsMatch(selectedBounds, bookmarkBounds);
            });
          },
        );

        // Both conditions must be true for a complete match
        if (
          allBookmarkPolygonsMatch &&
          allSelectedPolygonsMatch &&
          bookmarkPolygons.length === selectedPolygons.length
        ) {
          containsBookmark = true;
          bookmarkContained = bookmark;
          break; // Exit loop once match is found
        }
      }
    }
  }

  return { containsBookmark, bookmarkContained };
};
